import { useEffect, useState, useMemo } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "noval";
import { isHybrid } from "@network/http";
import BaseLayout from "@components/layouts/BaseLayout";
import AuthLayout from "@components/layouts/AuthLayout";
import { useSafeRouting } from "Hooks/useSafeRouting";
import {
  useAuthenticationDispatcher,
  useGetEnums,
} from "Hooks/globalsRequestHooks";

import { useResizeStorageListener } from "Hooks/useResizeStorageListener";
import { RenderModal, useRenderModal } from "@components/modals";
import { useIosPortal } from "Hooks/useIosPortal";
import { getCookiesApproval } from "./modals/CookiesSettingsModal/helper";

const Authentication = ({ children, lang }) => {
  const { dispatch } = useDispatch();
  const { query, pathname } = useRouter();

  const isPublicRoute = useMemo(() => isPublic(pathname), [pathname]);
  const isViewer = useMemo(() => pathname === "/viewer/[fileId]", [pathname]);

  const isIndex = useMemo(
    () => pathname === "/" || pathname === "",
    [pathname]
  );

  const isHybridPages = useMemo(
    () => isIndex || isHybrid(pathname),
    [isIndex, pathname]
  );
  const isHybridRoute = useMemo(
    () => isViewer || isHybridPages,
    [isViewer, isHybridPages]
  );

  const isPublicPages = useMemo(
    () => isViewer || isPublicRoute,
    [isViewer, isPublicRoute]
  );

  const [cookiesApproval, setCookiesApproval] = useState(getCookiesApproval());

  const handleCookiesApproval = () => {
    setCookiesApproval(true);
  };

  const isNotApp = query.app !== "true";
  // Hook to manage Appearence of Modals
  const renderedModals = useRenderModal(cookiesApproval, handleCookiesApproval);
  // Hook to manage user routing [authorized, not authorized]
  useSafeRouting(isPublicRoute, isHybridRoute);
  // Hook to get enums request
  useGetEnums(lang);
  // Hook to manage screen resize breakpoints and cookies storage effects
  useResizeStorageListener(setCookiesApproval, getCookiesApproval);
  // Hook to manage browse website from Ios agents
  useIosPortal(isNotApp);
  // Hook to manage and dispatch Authentication state
  useAuthenticationDispatcher();

  useEffect(() => {
    return () => {
      if (window.Intercom) {
        window.Intercom("hide");
        dispatch({ supportOpen: false });
      }
    };
  }, [pathname]);

  return (
    <>
      {isHybridPages ? (
        <BaseLayout>{children}</BaseLayout>
      ) : isPublicPages ? (
        <div className="min-h-[100vh] flex justify-center items-center">
          {children}
        </div>
      ) : (
        <AuthLayout>{children}</AuthLayout>
      )}

      {renderedModals.map((modal, key) => (
        <RenderModal key={key} modal={modal} />
      ))}
    </>
  );
};

export const isPublic = (url) => {
  const urls = [
    /^\/login/,
    /^\/register/,
    /^\/company-register/,
    /^\/forgot/,
    /^\/reset/,
    /^\/500/,
    /^\/404/,
    /^\/401/,
  ];

  return urls.some((path) => path.test(url));
};

export default Authentication;
